import React from 'react';

import {
  CheckInCardTestPeriodFragment,
  DashboardMostRecentLabSubmissionFragment,
  LabSubmissionStatusEnum,
  Maybe,
  MostRecentLabSubmissionWithResultsFragment,
} from '#apollo/schema';
import {useViewportSize} from '#hook';

import {
  AccountCardNarrow,
  ActionPlanCard,
  CheckInCard,
  CheckInCardCancelledMember,
  CheckInCardToPausedMember,
  MyTallyAgeCard,
  ReferAFriendCard,
  ReferralCardEntireRow,
  ReferralCardUpSellNarrow,
  RegisterTestCard,
  SetTracker,
  SurpriseByYourResultCard,
  TallyTestKitCard,
  UnderstandResultsCard,
  UnderstandResultsCompleted,
  UnlockActionPlans,
  UnlockActionPlansPausedMember,
  YourMembershipIsPausedNarrow,
} from './common';
import {processDashboardData} from './Dashboard.util';

/**
 * Active Member Dashboard
 */
export type ActiveMemberDashboardProps = {
  mostRecentLabSubmission: Maybe<DashboardMostRecentLabSubmissionFragment> | undefined;
  labSubmissions: Maybe<{id: string}[]> | undefined;
  currentTestPeriod: Maybe<CheckInCardTestPeriodFragment> | undefined;
  mostRecentLabSubmissionWithResults: Maybe<MostRecentLabSubmissionWithResultsFragment> | undefined;
};
export const ActiveMember = ({
  mostRecentLabSubmission,
  labSubmissions,
  currentTestPeriod,
  mostRecentLabSubmissionWithResults,
}: ActiveMemberDashboardProps) => {
  const {
    canCheckIn,
    hasSelectedActionPlan,
    selectedAdjustments,
    labAssessment,
    registeredDate,
    mostRecentStatus,
    hasLabSubmissions,
  } = processDashboardData({
    mostRecentLabSubmission,
    labSubmissions,
    currentTestPeriod,
    mostRecentLabSubmissionWithResults,
  });
  let referralCardEntireRow: React.ReactNode | null = null;

  referralCardEntireRow = <ReferralCardEntireRow />;

  if (mostRecentStatus && mostRecentStatus === LabSubmissionStatusEnum.Ready) {
    return (
      <>
        <CheckInCard
          canCheckIn={canCheckIn}
          completedCheckIns={
            currentTestPeriod?.checkInStats?.filter(({completed}) => !!completed).length || 0
          }
          hasSelectedActionPlan={hasSelectedActionPlan}
          columnSpan={[8, 4]}
          totalCheckIns={currentTestPeriod?.checkInStats?.length || 0}
        />
        <SurpriseByYourResultCard />
        <ActionPlanCard adjustments={selectedAdjustments} columnSpan={[8, 8]} />
        {labAssessment ? (
          <>
            <MyTallyAgeCard labAssessment={labAssessment} registeredDate={registeredDate} />
            <AccountCardNarrow />
            <ReferAFriendCard />
          </>
        ) : (
          <>
            <AccountCardNarrow />
            {referralCardEntireRow}
          </>
        )}
      </>
    );
  }
  if (mostRecentStatus && hasLabSubmissions) {
    return (
      <>
        <SetTracker
          labSubmissions={labSubmissions || []}
          mostRecentLabSubmission={mostRecentLabSubmission}
        />
        <UnderstandResultsCard hasLabAssessment={false} />
        <MyTallyAgeCard labAssessment={labAssessment} registeredDate={registeredDate} />
        <AccountCardNarrow />
        <ReferAFriendCard />
      </>
    );
  }
  return (
    <>
      <TallyTestKitCard />
      <RegisterTestCard />
      <UnderstandResultsCompleted hasLabAssessment={false} />
      <AccountCardNarrow />
    </>
  );
};

/**
 * Cancelled Member Dashboard
 */
export const CancelledMember = () => {
  const {isMobile, isTablet} = useViewportSize();
  return (
    <>
      <CheckInCardCancelledMember />
      <UnlockActionPlans />
      <SurpriseByYourResultCard />
      {isMobile || isTablet ? <ReferAFriendCard /> : <ReferralCardUpSellNarrow />}
    </>
  );
};

/**
 * Paused Member Dashboard
 */
export const PausedMember = () => {
  return (
    <>
      <YourMembershipIsPausedNarrow />
      <UnlockActionPlansPausedMember />
      <ReferAFriendCard />
      <CheckInCardToPausedMember />
    </>
  );
};
